// ═══════════════════════════════════════════════════════════════════════
// DEMO OUTCOMES — "what becomes possible" timeline per demo
// ═══════════════════════════════════════════════════════════════════════
// Surfaced on demo detail pages above the region picker / CTAs. The
// intent is to let a prospect see concretely what changes in their
// business on Week 1, Month 1, Quarter 1 — before they click into the
// live Operating System.
//
// Writing discipline:
// - Concrete, not aspirational. "Every deal has a clear next step"
//   not "transform your dealflow."
// - Category-specific. No generic language that could be pasted into
//   any Operating System. A PE outcome must read like something a partner would say.
// - Outcome-oriented, not feature-oriented. "You stop re-typing buyer
//   briefs into 3 systems" beats "Unified CRM."
// - Honest about tempo. Week 1 is "your existing data is in and
//   searchable." Quarter 1 is "patterns are showing up that you
//   couldn't see before."
// ═══════════════════════════════════════════════════════════════════════

export interface OutcomeBullet {
  label: string;      // short headline (e.g. "Pipeline consolidated")
  detail: string;     // one-line explanation
}

export interface DemoOutcome {
  week_1: OutcomeBullet[];     // what's true by end of first week
  month_1: OutcomeBullet[];    // what's true by end of first month
  quarter_1: OutcomeBullet[];  // what's true by end of first quarter
}

export const DEMO_OUTCOMES: Record<string, DemoOutcome> = {
  // ─── FOOTBALL AGENCY Operating System ────────────────────────────────────────────
  "football-agency-os": {
    week_1: [
      { label: "Every player in one pipeline", detail: "Your roster and prospects move out of spreadsheets, WhatsApp, and Notion into one kanban by stage." },
      { label: "Club relationships logged", detail: "Last touch, open negotiations, and payment status per club — visible in one place." },
      { label: "Contract deadlines surfaced", detail: "Every expiry, option window, and renegotiation date lands on your dashboard the moment the data's in." },
    ],
    month_1: [
      { label: "AI drafts the outreach", detail: "When a club has a need matching your roster, the system drafts the intro and cites the player data it used." },
      { label: "Commission forecast, not guesswork", detail: "Expected commission by quarter, with confidence based on stage. No more reverse-engineering from memory." },
      { label: "Scout reports become searchable", detail: "Upload a PDF scout report and it gets tagged against pathway filters so the right player surfaces next time." },
    ],
    quarter_1: [
      { label: "Your agency pattern becomes visible", detail: "Which clubs close, which positions you place fastest, where deals stall. The data shape of your business." },
      { label: "Intros get smarter over time", detail: "The system learns which club-player pairings you actually close. Lower-signal matches drop off the suggestions." },
      { label: "One source of truth before every board call", detail: "Quarterly reporting to partners or family office stops being a week-long spreadsheet exercise." },
    ],
  },

  // ─── REALTY Operating System ─────────────────────────────────────────────────────
  "realty-os": {
    week_1: [
      { label: "Buyers and listings in one view", detail: "No more switching between MLS, a CRM, and your notes. Buyer needs match to active listings on sight." },
      { label: "Every lead has a next step", detail: "The system tells you who needs a follow-up today, not a list of 80 stale contacts sorted by date." },
      { label: "Compliance paper trail started", detail: "RERA / FCA / state filings logged per deal from day one instead of pieced together at close." },
    ],
    month_1: [
      { label: "Buyer enquiries auto-respond with matches", detail: "A new enquiry gets a drafted reply with 3 listings matching their brief — ready for your sign-off in seconds." },
      { label: "Viewings stop clashing", detail: "Every agent on your team sees every viewing. Double-bookings and forgotten keys disappear." },
      { label: "Seller updates on autopilot", detail: "Weekly seller updates auto-draft with real activity data from the listing. You edit and send in 2 minutes." },
    ],
    quarter_1: [
      { label: "Your commission pipeline is forecastable", detail: "Projected close rate by stage, per agent, per neighborhood. The fuzzy part of the business gets measurable." },
      { label: "Referral network becomes an asset", detail: "Every past client and their introduction history is one tag-search away. Warm referrals stop slipping." },
      { label: "Pattern of your market emerges", detail: "Which areas sell fastest, which buyer budgets close reliably, which listings stall — showing up automatically." },
    ],
  },

  // ─── PROPERTY MANAGEMENT Operating System ────────────────────────────────────────
  "property-management-os": {
    week_1: [
      { label: "Every tenancy in one system", detail: "Lease dates, rent amounts, arrears, contact — pulled together from however many spreadsheets it lives in today." },
      { label: "Maintenance tickets stop getting lost", detail: "Tenant texts, emails, and WhatsApps route into one queue with priority and vendor assignment." },
      { label: "Landlord statements preview-ready", detail: "Monthly statement data is live from the first day. No more reconstruction at month-end." },
    ],
    month_1: [
      { label: "Renewals flag themselves 60 days out", detail: "No more 'the lease expired last week.' Every renewal conversation starts on time with the right context." },
      { label: "Arrears get chased consistently", detail: "Automated, polite first-contact drafts for late tenants. You review and approve, the system sends." },
      { label: "Vendor performance is visible", detail: "Average ticket-to-resolution time per vendor. The slow ones stop getting repeat work." },
    ],
    quarter_1: [
      { label: "Portfolio-level picture comes into focus", detail: "Occupancy, arrears, cap-ex trends across every building. The view landlords have been asking for." },
      { label: "Compliance audits become routine", detail: "Safety certs, gas checks, EPCs — every document traceable to source and expiry, not rebuilt from receipts." },
      { label: "Tenant satisfaction becomes measurable", detail: "Response times, repeat issues, renewal rate — the inputs that actually predict NPS show up as data." },
    ],
  },

  // ─── ACADEMY INTELLIGENCE Operating System ───────────────────────────────────────
  "academy-intelligence-os": {
    week_1: [
      { label: "Every player profile consolidated", detail: "Scout reports, session notes, medical, parent contacts — per player, in one place." },
      { label: "Session attendance stops being paper", detail: "Coaches log from phones, attendance is live, parent enquiries stop catching staff off-guard." },
      { label: "Scouting interests tracked", detail: "Which clubs are watching which players, last contact, what they asked for. Out of DMs into a pipeline." },
    ],
    month_1: [
      { label: "Development plans become visible", detail: "Per-player goals, progress, coach ratings — so a 15-year-old's pathway isn't in one coach's head." },
      { label: "Parent communication automates politely", detail: "Fee reminders, schedule updates, progress summaries drafted and ready. You stay in control of voice." },
      { label: "Coach compliance surfaces early", detail: "License expiry, safeguarding certs, first-aid renewals — no surprise audit gaps." },
    ],
    quarter_1: [
      { label: "Your academy's identity becomes data", detail: "Placement rates, which positions you develop best, which age groups have retention issues. Actionable patterns." },
      { label: "Player journeys tell a story", detail: "From U11 to first team or exit — the full arc is queryable for parents, scouts, and your own planning." },
      { label: "Revenue and cost per cohort is clear", detail: "Which age groups subsidize which, where the program runs hot, where it doesn't. Finance stops being opaque." },
    ],
  },

  // ─── LUXURY DESIGN Operating System ──────────────────────────────────────────────
  "luxury-design-os": {
    week_1: [
      { label: "Every active project in one view", detail: "Stage, lead designer, client, timeline — pulled out of Asana, Dropbox, and email into one board." },
      { label: "Client approvals leave a trail", detail: "Every sign-off, every revision request, every furniture spec decision tied to the right project and person." },
      { label: "Moodboard library becomes searchable", detail: "Past projects, palettes, material combinations tagged by style. Reference a 'Dubai penthouse palette' and the answer is there." },
    ],
    month_1: [
      { label: "Procurement stops slipping", detail: "Lead times, vendor confirmations, shipment tracking — all tied to the project. Install date stops being a prayer." },
      { label: "Client concierge becomes scalable", detail: "AI drafts the thoughtful follow-ups clients expect, in your studio's voice, not generic." },
      { label: "Designer utilization is visible", detail: "Who's on what, who's at capacity, which project is about to overrun. Scheduling stops being reactive." },
    ],
    quarter_1: [
      { label: "Studio economics become measurable", detail: "Gross margin per project, per client type, per designer — the numbers that decide which clients you pursue next year." },
      { label: "Client LTV shows up", detail: "Which private clients come back, which referrals deliver, which relationships compound. Your book becomes an asset class." },
      { label: "Your signature becomes codifiable", detail: "The patterns of material, palette, and proportion that define your studio — visible across projects, not just in your head." },
    ],
  },

  // ─── PE DEALFLOW Operating System ────────────────────────────────────────────────
  "pe-dealflow-os": {
    week_1: [
      { label: "Every deal in one dashboard", detail: "Sourced, screened, diligence, IC, invested — the stage of every live deal, visible to the whole team." },
      { label: "LP relationships consolidated", detail: "Commitments, call history, document access — per LP, in one place instead of three fund admin tools." },
      { label: "Portfolio company KPIs come home", detail: "Monthly metrics from each portco surface in one view, flagging when one starts missing covenants." },
    ],
    month_1: [
      { label: "IC memos draft themselves", detail: "The thesis template pulls deal data, DD notes, and comps into a memo skeleton. You add judgment, cut draft time in half." },
      { label: "Dealflow sources get measured", detail: "Which bankers, referrals, and proprietary channels actually produce deals that close — not just noise." },
      { label: "LP reporting gets predictable", detail: "Quarterly reports draft from the system. No more 72-hour scramble pulling numbers from analysts." },
    ],
    quarter_1: [
      { label: "Your thesis has proof", detail: "Pattern of what closes in your fund vs what doesn't — sector, check size, geography, partner lead. The fund strategy becomes data-backed." },
      { label: "Portfolio health is live", detail: "Companies needing attention flag themselves. Board prep becomes a review of live data, not a reconstruction." },
      { label: "Fund succession becomes possible", detail: "The partner-in-their-head dealflow knowledge becomes institutional memory the firm can grow around." },
    ],
  },

  // ─── TRADE DOCS Operating System ─────────────────────────────────────────────────
  "trade-docs-os": {
    week_1: [
      { label: "Every shipment in one tracking view", detail: "Quoted, booked, in transit, at customs, delivered — across all modes, carriers, and customers." },
      { label: "Document packets assemble themselves", detail: "BoL, commercial invoice, packing list, CoO — pulled together per shipment instead of typed out each time." },
      { label: "Customer visibility improves day one", detail: "Status link per shipment, auto-updated. The 'where's my cargo' calls start dropping immediately." },
    ],
    month_1: [
      { label: "Customs queries stop scrambling", detail: "HS code history, broker contact, past rulings per commodity — the context is there when the query lands." },
      { label: "Compliance paper trail audit-ready", detail: "Sanctions screening, duty calculations, CoO authenticity — logged per shipment, not reconstructed from email." },
      { label: "Partner performance becomes visible", detail: "Which carriers are on-time, which brokers clear fastest, which routes have exception rates. Contract renewals get leverage." },
    ],
    quarter_1: [
      { label: "Your trade lanes become optimizable", detail: "Cost per kg, per lane, per customer — with outlier shipments flagged for why. The edge margin appears." },
      { label: "Customer economics come into focus", detail: "Which customers are actually profitable at current rates, which need renegotiation, which are worth pursuing more of." },
      { label: "Risk exposure is quantified", detail: "Concentration by customer, by lane, by commodity. When something's happening in the Red Sea, you know immediately what it costs you." },
    ],
  },
};

// Resolve outcomes for a given demo id. Returns null if no outcomes
// configured — caller should hide the section rather than show empty.
export function getOutcomes(demoId: string): DemoOutcome | null {
  return DEMO_OUTCOMES[demoId] ?? null;
}
